import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useApi } from '../composables/useApi';
import { employeeService } from '../services/employeeService';
import { departmentService } from '../services/departmentService';
import { useToast } from '@/composables/useToast';
import { clientsApi } from '@/services/api';
import { useAuthStore } from '../stores/auth';

/**
 * Master Data Store - Centralized caching for reference data
 * 
 * This store provides intelligent caching for frequently accessed master data
 * including users, employees, clients, departments, and products to reduce
 * duplicate API calls across components and improve performance.
 */
export const useMasterDataStore = defineStore('masterData', () => {
  const api = useApi();
  const toast = useToast();

  // Cache storage with metadata
  const cache = ref(new Map());
  const cacheMetadata = ref(new Map());
  const pendingRequests = ref(new Map());
  const isInitialized = ref(false);

  // Cache configuration
  const config = {
    ttl: {
      users: 60 * 60 * 1000,         // 60 minutes
      employees: 60 * 60 * 1000,     // 60 minutes
      clients: 10 * 60 * 1000,       // 10 minutes
      departments: 60 * 60 * 1000,   // 60 minutes
      products: 30 * 60 * 1000,      // 30 minutes
      orderStatuses: 60 * 60 * 1000, // 60 minutes
    },
    backgroundRefresh: {
      enabled: true,
      threshold: 0.75, // Refresh when 75% of TTL has elapsed
      maxConcurrent: 2, // Max concurrent background refreshes
    },
    maxCacheSize: 1000,
    cleanupInterval: 30 * 60 * 1000, // 30 minutes
  };

  // Performance tracking
  const stats = ref({
    hits: 0,
    misses: 0,
    backgroundRefreshes: 0,
    errors: 0,
    totalRequests: 0,
  });

  let cleanupInterval = null;
  let activeBackgroundRefreshes = 0;

  // ===== CACHE MANAGEMENT =====

  /**
   * Get data from cache or fetch if not available/expired
   */
  async function getCachedData(dataType, options = {}) {
    const cacheKey = generateCacheKey(dataType, options);
    stats.value.totalRequests++;

    try {
      // Load dependencies first if needed
      if (options.loadDependencies !== false) {
        await loadDependencies(dataType, options);
      }

      // Check for valid cached data first
      const cachedResult = getCachedResult(cacheKey);
      if (cachedResult !== null) {
        stats.value.hits++;
        console.log(`[MasterData] Cache hit for ${cacheKey}`);

        // Schedule background refresh if needed
        scheduleBackgroundRefresh(cacheKey, dataType, options);

        return cachedResult;
      }

      // Check if the same request is already pending (deduplication)
      if (pendingRequests.value.has(cacheKey)) {
        console.log(`[MasterData] Deduplicating concurrent request for ${cacheKey}`);
        return await pendingRequests.value.get(cacheKey);
      }

      // Cache miss - fetch fresh data
      stats.value.misses++;
      console.log(`[MasterData] Cache miss for ${cacheKey}, fetching...`);

      const fetchPromise = fetchDataByType(dataType, options);
      pendingRequests.value.set(cacheKey, fetchPromise);

      try {
        const result = await fetchPromise;
        setCachedResult(cacheKey, result, dataType);
        return result;
      } finally {
        pendingRequests.value.delete(cacheKey);
      }

    } catch (error) {
      // Don't log or count permission errors (403) - these are expected for users without permissions
      if (error.response?.status === 403 || error.message === 'Permission denied') {
        console.log(`[MasterData] Permission denied for ${cacheKey}, skipping fetch`);
        throw error; // Still throw so calling code can handle it
      }

      stats.value.errors++;
      console.error(`[MasterData] Error fetching ${cacheKey}:`, error);

      // Try to return stale data as fallback
      const staleData = getStaleResult(cacheKey);
      if (staleData !== null) {
        console.log(`[MasterData] Returning stale data for ${cacheKey}`);
        toast.warning('Using cached data - refresh in progress');
        return staleData;
      }

      throw error;
    }
  }

  /**
   * Get cached result if valid
   */
  function getCachedResult(cacheKey) {
    const cached = cache.value.get(cacheKey);
    if (!cached) return null;

    // Update access metadata
    cached.lastAccessed = Date.now();
    cached.accessCount++;

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      console.log(`[MasterData] Cache expired for ${cacheKey}`);
      return null;
    }

    return cached.data;
  }

  /**
   * Get stale result (expired but still in cache)
   */
  function getStaleResult(cacheKey) {
    const cached = cache.value.get(cacheKey);
    return cached ? cached.data : null;
  }

  /**
   * Set cached result with metadata
   */
  function setCachedResult(cacheKey, data, dataType) {
    const ttl = config.ttl[dataType] || config.ttl.users;
    const expiresAt = Date.now() + ttl;

    const cacheEntry = {
      data,
      expiresAt,
      createdAt: Date.now(),
      lastAccessed: Date.now(),
      accessCount: 1,
    };

    const metadataEntry = {
      dataType,
      ttl,
      refreshable: true,
    };

    // Cache with the provided key
    cache.value.set(cacheKey, cacheEntry);
    cacheMetadata.value.set(cacheKey, metadataEntry);

    // Also cache with simple key ONLY if the current request WAS for the simple key
    // This prevents filtered results (e.g. "search=xyz") from overwriting the main "all users" cache
    if (cacheKey === dataType && data && data.length > 0) {
      // It's safe to update the simple key because that's exactly what we requested
      // console.log(`[MasterData] Updating simple cache for ${dataType}`);
    } else if (cacheKey !== dataType && data && data.length > 0) {
      // DEBUG: Log that we are NOT overwriting the main cache with this filtered result
      // console.log(`[MasterData] NOT updating simple cache for ${dataType} with filtered result from ${cacheKey}`);
    }

    console.log(`[MasterData] Cached ${cacheKey} with TTL ${ttl}ms`);
    manageCacheSize();
  }

  /**
   * Generate cache key
   */
  function generateCacheKey(dataType, options = {}) {
    // For basic requests without significant filtering, use simple cache keys
    // Note: For products and orderStatuses, basic active filtering is considered "normal" and doesn't need cache key differentiation
    const hasSignificantParams =
      (options.search && options.search.trim() !== '') ||
      (options.classification && options.classification !== '') ||
      (options.payment_options && options.payment_options !== '') ||
      (options.approval_status && options.approval_status !== '') ||
      (options.department_id && options.department_id !== '') ||
      (options.role && options.role !== '') ||
      (options.active !== undefined && dataType !== 'products') || // Active filter is normal for products
      // For clients, pagination and sorting are significant
      (dataType === 'clients' && (options.page || options.limit || options.sort_by || options.sort_order || options.is_active));

    // If no significant filtering parameters, use simple cache key
    if (!hasSignificantParams) {
      return dataType;
    }

    // Otherwise, include the parameters in the cache key
    const params = new URLSearchParams();

    // Add relevant options to cache key
    if (options.active !== undefined) params.set('active', options.active);
    if (options.department_id) params.set('department_id', options.department_id);
    if (options.role) params.set('role', options.role);

    // Client-specific parameters
    if (options.search !== undefined && options.search !== '') params.set('search', options.search);
    if (options.limit !== undefined) params.set('limit', options.limit);
    if (options.page !== undefined) params.set('page', options.page);
    if (options.classification !== undefined && options.classification !== '') params.set('classification', options.classification);
    if (options.is_active !== undefined && options.is_active !== '') params.set('is_active', options.is_active);
    if (options.sort_by !== undefined) params.set('sort_by', options.sort_by);
    if (options.sort_order !== undefined) params.set('sort_order', options.sort_order);
    if (options.payment_options !== undefined && options.payment_options !== '') params.set('payment_options', options.payment_options);
    if (options.approval_status !== undefined && options.approval_status !== '') params.set('approval_status', options.approval_status);

    const paramsString = params.toString();
    return paramsString ? `${dataType}?${paramsString}` : dataType;
  }

  // ===== DATA FETCHERS =====

  /**
   * Fetch data by type
   */
  async function fetchDataByType(dataType, options = {}) {
    switch (dataType) {
      case 'users':
        return await fetchUsers(options);
      case 'assignableUsers':
        return await fetchAssignableUsers(options);
      case 'employees':
        return await fetchEmployees(options);
      case 'clients':
        return await fetchClients(options);
      case 'departments':
        return await fetchDepartments(options);
      case 'products':
        return await fetchProducts(options);
      case 'orderStatuses':
        return await fetchOrderStatuses(options);
      default:
        throw new Error(`Unknown data type: ${dataType}`);
    }
  }

  /**
   * Fetch users data
   */
  async function fetchUsers(options = {}) {
    const result = await employeeService.getAllSystemUsers();
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch users');
    }
    return result.data;
  }

  /**
   * Fetch assignable users data (for order creation - requires only orders.create permission)
   */
  async function fetchAssignableUsers(options = {}) {
    const result = await employeeService.getAssignableUsers();
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch assignable users');
    }
    return result.data;
  }

  /**
   * Fetch employees data
   */
  async function fetchEmployees(options = {}) {
    // Check permission before attempting to fetch
    if (!hasPermissionForDataType('employees')) {
      console.log('[MasterData] User does not have employees.view permission, skipping fetch');
      throw new Error('Permission denied');
    }

    const result = await employeeService.getAll();
    if (!result.success) {
      // If it's a permission error, throw a specific error that can be caught
      if (result.message === 'Permission denied') {
        throw new Error('Permission denied');
      }
      throw new Error(result.message || 'Failed to fetch employees');
    }
    return result.data;
  }

  /**
   * Fetch clients data
   */
  async function fetchClients(options = {}) {
    try {
      // Different client API patterns based on the calling component
      if (options.useDirectApi) {
        // SalesDashboard.vue pattern: api.get('/clients')
        const response = await api.get('/clients', {
          params: {
            search: options.search,
            limit: options.limit || 100,
            approval_status: options.approval_status || 'approved'
          }
        });
        return response.data.data || [];
      } else {
        // Quotations.vue and Clients.vue pattern: clientsApi.getAll()
        const response = await clientsApi.getAll({
          search: options.search,
          limit: options.limit,
          page: options.page,
          classification: options.classification,
          is_active: options.is_active,
          sort_by: options.sort_by,
          sort_order: options.sort_order,
          payment_options: options.payment_options,
          approval_status: options.approval_status || 'approved',
          updated_since: options.updated_since
        });

        console.log('[MasterData] Clients API response structure:', {
          hasData: !!response.data,
          hasPagination: !!response.data?.pagination,
          dataLength: response.data?.data?.length,
          total: response.data?.pagination?.total
        });

        // For paginated requests, return full response data including pagination metadata
        if (options.page || options.limit) {
          // Map backend response structure to frontend expected structure
          const backendResponse = response.data;
          const mappedResponse = {
            data: backendResponse.data || [],
            total: backendResponse.pagination?.total || 0,
            page: backendResponse.pagination?.current_page || 1,
            limit: backendResponse.pagination?.per_page || 10,
            totalPages: backendResponse.pagination?.total_pages || 1
          };

          console.log('[MasterData] Mapped pagination response:', mappedResponse);
          return mappedResponse;
        }

        // For non-paginated requests, return just the data array
        return response.data?.data || [];
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch clients');
    }
  }

  /**
   * Fetch departments data
   */
  async function fetchDepartments(options = {}) {
    const result = options.active
      ? await departmentService.getActive()
      : await departmentService.getAll();

    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch departments');
    }
    return result.data;
  }

  /**
   * Fetch products data
   */
  async function fetchProducts(options = {}) {
    try {
      const params = {};

      // Only add parameters if they're explicitly provided
      if (options.status) params.status = options.status;
      if (options.is_active !== undefined) params.status = options.is_active ? 'active' : 'inactive';
      if (options.limit) {
        params.limit = options.limit;
      } else {
        // If no limit specified, use a very high limit to get all products
        // This is for cases like product selection where we need all products
        params.limit = 10000;
      }
      if (options.category_id) params.category = options.category_id;
      if (options.search) params.search = options.search;
      if (options.page) params.page = options.page;

      // If no specific status is requested, default to active
      if (!params.status) params.status = 'active';

      const response = await api.get('/products', { params });
      return response.data.data || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch products');
    }
  }

  /**
   * Fetch order statuses
   */
  async function fetchOrderStatuses(options = {}) {
    try {
      const response = await api.get('/order-status');
      return response.data || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order statuses');
    }
  }

  // ===== DEPENDENCY LOADING =====

  /**
   * Check if user has permission for a data type
   */
  function hasPermissionForDataType(dataType) {
    try {
      const authStore = useAuthStore();
      if (!authStore.user) return false;

      const permissionMap = {
        'users': 'users.view',
        'employees': 'employees.view',
        'clients': 'clients.view',
        'products': 'products.view',
        'orders': 'orders.view',
        'payments': 'payments.view',
        'quotations': 'quotations.view'
      };

      const requiredPermission = permissionMap[dataType];
      if (!requiredPermission) return true; // No permission required

      return authStore.hasPermission(requiredPermission);
    } catch (error) {
      // If auth store is not available, assume no permission
      console.warn('[MasterData] Could not check permission for', dataType, error);
      return false;
    }
  }

  /**
   * Load dependencies for a given data type
   */
  async function loadDependencies(dataType, options = {}) {
    const dependencies = getDependencies(dataType);

    if (dependencies.length === 0) return;

    console.log(`[MasterData] Loading dependencies for ${dataType}:`, dependencies);

    try {
      // Load dependencies in parallel (they're independent of each other)
      const dependencyPromises = dependencies.map(async (depType) => {
        // Check permission before loading dependency
        if (!hasPermissionForDataType(depType)) {
          console.log(`[MasterData] Skipping dependency ${depType} - user lacks required permission`);
          return null;
        }

        const depCacheKey = generateCacheKey(depType, {});

        // Only load if not already cached or if forced
        if (!getCachedResult(depCacheKey) || options.forceDependencies) {
          console.log(`[MasterData] Loading dependency: ${depType}`);
          try {
            return await getCachedData(depType, { loadDependencies: false }); // Prevent infinite recursion
          } catch (error) {
            // If it's a permission error (403), skip silently
            if (error.response?.status === 403 || error.message?.includes('403')) {
              console.log(`[MasterData] Skipping dependency ${depType} due to permission error`);
              return null;
            }
            // Re-throw other errors
            throw error;
          }
        }

        return null;
      });

      await Promise.all(dependencyPromises);
      console.log(`[MasterData] Dependencies loaded for ${dataType}`);

    } catch (error) {
      // Only log non-permission errors
      if (error.response?.status !== 403 && !error.message?.includes('403')) {
        console.warn(`[MasterData] Some dependencies failed to load for ${dataType}:`, error);
      }
      // Don't throw - dependencies are optional enhancements
    }
  }

  /**
   * Get dependency list for a data type
   */
  function getDependencies(dataType) {
    const dependencyMap = {
      // When loading employees, also load departments and users
      employees: ['departments', 'users'],

      // When loading orders, also load clients, users, products, and order statuses
      orders: ['clients', 'users', 'products', 'orderStatuses'],

      // When loading products, also load departments (for categorization)
      products: ['departments'],

      // When loading payments, also load clients and orders
      payments: ['clients', 'orders'],

      // Quotations depend on clients and products
      quotations: ['clients', 'products'],

      // User lookups might benefit from having employee data
      users: ['departments'],
    };

    return dependencyMap[dataType] || [];
  }

  /**
   * Intelligent data preloading based on usage patterns
   */
  async function preloadCommonData() {
    console.log('[MasterData] Starting intelligent preload of common data');

    try {
      // Preload the most commonly used data in order of importance
      const preloadSequence = [
        { type: 'users', priority: 1 },           // Highest priority - used everywhere
        { type: 'departments', priority: 1 },     // High priority - used in forms
        { type: 'clients', priority: 2 },         // Medium-high - used in dashboards
        { type: 'orderStatuses', priority: 2 },   // Medium-high - used in status dropdowns
        { type: 'products', priority: 3 },        // Medium - used in order forms
      ];

      // Load high priority items first (parallel)
      const highPriority = preloadSequence.filter(item => item.priority === 1);
      await Promise.all(
        highPriority.map(item =>
          getCachedData(item.type, { loadDependencies: false, preload: true })
        )
      );

      // Then medium-high priority
      const mediumHighPriority = preloadSequence.filter(item => item.priority === 2);
      await Promise.all(
        mediumHighPriority.map(item =>
          getCachedData(item.type, { loadDependencies: false, preload: true })
        )
      );

      // Finally medium priority (can be done in background)
      const mediumPriority = preloadSequence.filter(item => item.priority === 3);
      Promise.all(
        mediumPriority.map(item =>
          getCachedData(item.type, { loadDependencies: false, preload: true })
        )
      ).catch(error => {
        console.warn('[MasterData] Medium priority preload failed:', error);
      });

      console.log('[MasterData] Common data preload completed');

    } catch (error) {
      console.warn('[MasterData] Preload failed, continuing normally:', error);
    }
  }

  // ===== BACKGROUND REFRESH =====

  /**
   * Schedule background refresh if needed
   */
  function scheduleBackgroundRefresh(cacheKey, dataType, options) {
    if (!config.backgroundRefresh.enabled) return;
    if (activeBackgroundRefreshes >= config.backgroundRefresh.maxConcurrent) return;

    const cached = cache.value.get(cacheKey);
    if (!cached) return;

    const timeElapsed = Date.now() - cached.createdAt;
    const refreshThreshold = config.ttl[dataType] * config.backgroundRefresh.threshold;

    if (timeElapsed > refreshThreshold) {
      activeBackgroundRefreshes++;
      backgroundRefresh(cacheKey, dataType, options);
    }
  }

  /**
   * Perform background refresh
   */
  async function backgroundRefresh(cacheKey, dataType, options) {
    try {
      console.log(`[MasterData] Background refresh for ${cacheKey}`);
      stats.value.backgroundRefreshes++;

      const freshData = await fetchDataByType(dataType, options);
      setCachedResult(cacheKey, freshData, dataType);

    } catch (error) {
      console.warn(`[MasterData] Background refresh failed for ${cacheKey}:`, error);
    } finally {
      activeBackgroundRefreshes--;
    }
  }

  // ===== CACHE INVALIDATION =====

  /**
   * Invalidate cache by pattern or specific key
   */
  function invalidate(pattern) {
    const keysToDelete = [];

    for (const [key] of cache.value) {
      if (typeof pattern === 'string') {
        if (key.includes(pattern)) {
          keysToDelete.push(key);
        }
      } else if (pattern instanceof RegExp) {
        if (pattern.test(key)) {
          keysToDelete.push(key);
        }
      }
    }

    keysToDelete.forEach(key => {
      cache.value.delete(key);
      cacheMetadata.value.delete(key);
    });

    console.log(`[MasterData] Invalidated ${keysToDelete.length} cache entries`);
    return keysToDelete.length;
  }

  /**
   * Invalidate cache on data changes
   */
  function invalidateOnChange(dataType, operation = null) {
    const invalidationMap = {
      user: ['users', 'employees'],
      employee: ['employees', 'users'],
      client: ['clients'],
      department: ['departments'],
      product: ['products'],
      order: ['orderStatuses'],
    };

    const typesToInvalidate = invalidationMap[dataType] || [dataType];
    let totalInvalidated = 0;

    typesToInvalidate.forEach(type => {
      totalInvalidated += invalidate(type);
    });

    console.log(`[MasterData] Invalidated ${totalInvalidated} entries due to ${dataType} ${operation || 'change'}`);
  }

  // ===== CACHE MANAGEMENT =====

  /**
   * Manage cache size by removing least used items
   */
  function manageCacheSize() {
    if (cache.value.size <= config.maxCacheSize) return;

    const entries = Array.from(cache.value.entries()).map(([key, data]) => ({
      key,
      data,
      metadata: cacheMetadata.value.get(key),
      score: calculateEvictionScore(data, cacheMetadata.value.get(key)),
    }));

    // Sort by eviction score (higher score = more likely to evict)
    entries.sort((a, b) => b.score - a.score);

    const toRemove = entries.slice(config.maxCacheSize * 0.8); // Remove 20% when over limit
    toRemove.forEach(({ key }) => {
      cache.value.delete(key);
      cacheMetadata.value.delete(key);
    });

    console.log(`[MasterData] Evicted ${toRemove.length} cache entries`);
  }

  /**
   * Calculate eviction score (higher = more likely to evict)
   */
  function calculateEvictionScore(cacheEntry, metadata) {
    const now = Date.now();
    const age = now - cacheEntry.createdAt;
    const lastAccess = now - cacheEntry.lastAccessed;
    const accessFrequency = cacheEntry.accessCount / (age / 60000); // per minute

    // Factors: age (40%), last access time (30%), access frequency (30%)
    return (age * 0.4) + (lastAccess * 0.3) + ((1 / (accessFrequency + 1)) * 0.3);
  }

  /**
   * Start cleanup scheduler
   */
  function startCleanupScheduler() {
    if (cleanupInterval) return;

    cleanupInterval = setInterval(() => {
      performCleanup();
    }, config.cleanupInterval);

    console.log('[MasterData] Cleanup scheduler started');
  }

  /**
   * Perform cache cleanup
   */
  function performCleanup() {
    const now = Date.now();
    const keysToDelete = [];

    for (const [key, cached] of cache.value) {
      if (now > cached.expiresAt) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => {
      cache.value.delete(key);
      cacheMetadata.value.delete(key);
    });

    if (keysToDelete.length > 0) {
      console.log(`[MasterData] Cleaned up ${keysToDelete.length} expired entries`);
    }
  }

  // ===== PUBLIC API =====

  /**
   * Initialize the store
   */
  function initialize() {
    if (isInitialized.value) return;

    startCleanupScheduler();
    isInitialized.value = true;

    console.log('[MasterData] Store initialized');
  }

  // ===== COMPUTED GETTERS =====

  /**
   * Get cached data for a type, checking all possible cache keys
   */
  function getAggregatedCacheData(dataType) {
    // First try the simple cache key
    let data = getCachedResult(dataType);
    if (data && data.length > 0) {
      console.log(`[MasterData] Found ${dataType} data in simple cache key (${data.length} items)`);
      return data;
    }

    // If not found, look for any cache key that starts with the dataType
    const availableKeys = Array.from(cache.value.keys()).filter(key => key.startsWith(dataType));
    console.log(`[MasterData] Looking for ${dataType} data, available cache keys:`, availableKeys);

    for (const [cacheKey, cached] of cache.value) {
      if (cacheKey.startsWith(dataType) && !expired(cached)) {
        data = cached.data;
        if (data && data.length > 0) {
          console.log(`[MasterData] Found ${dataType} data in cache key: ${cacheKey} (${data.length} items)`);
          return data;
        }
      }
    }

    console.log(`[MasterData] No ${dataType} data found in cache`);
    return [];
  }

  /**
   * Check if cache entry is expired
   */
  function expired(cached) {
    return Date.now() > cached.expiresAt;
  }

  // Users
  const users = computed(() => getAggregatedCacheData('users'));
  const activeUsers = computed(() => users.value.filter(user => user.is_active));

  // Employees
  const employees = computed(() => getAggregatedCacheData('employees'));
  const activeEmployees = computed(() => employees.value.filter(emp => emp.is_active !== false));

  // Clients
  const clients = computed(() => getAggregatedCacheData('clients'));
  const activeClients = computed(() => clients.value.filter(client => client.is_active !== false));

  // Departments
  const departments = computed(() => getAggregatedCacheData('departments'));
  const activeDepartments = computed(() => departments.value.filter(dept => dept.is_active !== false));

  // Products
  const products = computed(() => getAggregatedCacheData('products'));
  const activeProducts = computed(() => products.value.filter(product => product.is_active !== false));

  // Order Statuses
  const orderStatuses = computed(() => getAggregatedCacheData('orderStatuses'));

  // ===== HELPER FUNCTIONS =====

  /**
   * Get user by ID
   */
  function getUserById(id) {
    return users.value.find(user => user.id === id) || null;
  }

  /**
   * Get employee by ID
   */
  function getEmployeeById(id) {
    return employees.value.find(emp => emp.id === id) || null;
  }

  /**
   * Get employee by user ID
   */
  function getEmployeeByUserId(userId) {
    return employees.value.find(emp => emp.user_id === userId) || null;
  }

  /**
   * Get client by ID
   */
  function getClientById(id) {
    return clients.value.find(client => client.id === id) || null;
  }

  /**
   * Get department by ID
   */
  function getDepartmentById(id) {
    return departments.value.find(dept => dept.id === id) || null;
  }

  /**
   * Get product by ID
   */
  function getProductById(id) {
    return products.value.find(product => product.id === id) || null;
  }

  /**
   * Get cache statistics
   */
  function getStats() {
    const hitRate = stats.value.totalRequests > 0
      ? (stats.value.hits / stats.value.totalRequests * 100).toFixed(2)
      : 0;

    return {
      ...stats.value,
      hitRate: `${hitRate}%`,
      cacheSize: cache.value.size,
      metadataSize: cacheMetadata.value.size,
      activeBackgroundRefreshes,
    };
  }

  /**
   * Clear all cache
   */
  function clearCache() {
    cache.value.clear();
    cacheMetadata.value.clear();
    pendingRequests.value.clear();
    console.log('[MasterData] Cache cleared');
  }

  /**
   * Cleanup on store destruction
   */
  function cleanup() {
    if (cleanupInterval) {
      clearInterval(cleanupInterval);
      cleanupInterval = null;
    }
    clearCache();
    console.log('[MasterData] Store cleaned up');
  }

  // Auto-initialize when store is created
  initialize();

  return {
    // State
    isInitialized,

    // Actions
    getCachedData,
    generateCacheKey,
    getCachedResult,
    invalidate,
    invalidateOnChange,
    initialize,
    clearCache,
    cleanup,

    // Dependency and preloading
    preloadCommonData,
    loadDependencies,

    // Computed getters
    users,
    activeUsers,
    employees,
    activeEmployees,
    clients,
    activeClients,
    departments,
    activeDepartments,
    products,
    activeProducts,
    orderStatuses,

    // Helper functions
    getUserById,
    getEmployeeById,
    getEmployeeByUserId,
    getClientById,
    getDepartmentById,
    getProductById,
    getStats,
  };
});

export default useMasterDataStore; 